import React, { useEffect, useRef } from 'react';
import { useTrainingSocket } from '../../context/TrainingSocketContext';
import { socketService } from '../../services/trainingSocketService';
import PixiNetworkRendererClass from '../PixiNetworkRendererClass';
import { TrainingProgressData } from '../../services/trainingSocketService';
import './NetworkVisualization2DComponent.css';

interface NetworkVisualization2DComponentProps {
  modelStructure?: any;
  trainingProgressData?: TrainingProgressData[];
}

/**
 * NetworkVisualization2DComponent
 *
 * Renders a 2D visualization of the neural network being trained using Pixi.js.
 * Receives model structure and training data as props from TrainingProgressDisplay.
 * This is a 2D version optimized for performance.
 */
const NetworkVisualization2DComponent: React.FC<
  NetworkVisualization2DComponentProps
> = ({ modelStructure, trainingProgressData = [] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<PixiNetworkRendererClass | null>(null);
  const hasRequestedStructure = useRef(false);
  const { modelStructure: contextModelStructure } = useTrainingSocket();

  // Request model structure from backend when component mounts
  useEffect(() => {
    if (!hasRequestedStructure.current) {
      hasRequestedStructure.current = true;
      console.log(
        '[NetworkViz 2D] Component mounted, requesting model structure from backend'
      );
      // Request the model structure from the backend
      socketService.emit('requestModelStructure');
      console.log('[NetworkViz 2D] Emitted requestModelStructure event');
    }
  }, []);

  // Initialize renderer with model structure (from context or props)
  useEffect(() => {
    const structure = contextModelStructure || modelStructure;
    console.log('[NetworkViz 2D] useEffect triggered, structure exists:', !!structure);
    console.log('[NetworkViz 2D] containerRef.current exists:', !!containerRef.current);
    console.log('[NetworkViz 2D] rendererRef.current exists:', !!rendererRef.current);

    if (containerRef.current && structure && !rendererRef.current) {
      console.log(
        '[NetworkViz 2D] Initializing renderer with model structure'
      );

      // Initialize PixiNetworkRenderer (async initialization)
      console.log('[NetworkViz 2D] Creating new PixiNetworkRendererClass...');
      const renderer = new PixiNetworkRendererClass(containerRef.current);
      rendererRef.current = renderer;
      console.log('[NetworkViz 2D] Renderer created');

      // Wait for async initialization to complete, then set structure
      renderer.waitForInitialization().then(() => {
        console.log('[NetworkViz 2D] Renderer initialization complete');
        if (rendererRef.current) {
          try {
            console.log('[NetworkViz 2D] Calling setNetworkStructure...');
            rendererRef.current.setNetworkStructure(structure);
            console.log('[NetworkViz 2D] setNetworkStructure completed');
          } catch (error) {
            console.error('[NetworkViz 2D] Error setting network structure:', error);
          }
        }
      }).catch(error => {
        console.error('[NetworkViz 2D] Renderer initialization failed:', error);
      });

      // Handle window resize
      const handleResize = () => {
        if (rendererRef.current) {
          try {
            rendererRef.current.handleResize();
          } catch (error) {
            console.error('[NetworkViz 2D] Error in handleResize:', error);
          }
        }
      };

      window.addEventListener('resize', handleResize);
      // Initial sizing after a brief delay to ensure initialization
      const resizeTimer = setTimeout(() => {
        handleResize();
      }, 200);

      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(resizeTimer);
        if (rendererRef.current) {
          try {
            rendererRef.current.cleanup();
          } catch (error) {
            console.error('[NetworkViz 2D] Error during cleanup:', error);
          }
        }
      };
    }
  }, [contextModelStructure, modelStructure]);

  // Update visualization when training progress data changes
  useEffect(() => {
    if (
      rendererRef.current &&
      trainingProgressData &&
      trainingProgressData.length > 0
    ) {
      const latestData = trainingProgressData[trainingProgressData.length - 1];
      rendererRef.current.updateTrainingData(latestData);
    }
  }, [trainingProgressData]);

  return (
    <div
      ref={containerRef}
      className="network-visualization-2d-component"
      style={{ width: '100%', height: '100%' }}
    />
  );
};

export default NetworkVisualization2DComponent;
